/*
  Custom variant dropdown.

  Dawn's VariantSelects listens for `change` on the real <select> and reads
  `option[selected]`, so the select is kept in the DOM and stays the source of truth.
  This element only paints a styled trigger + listbox over it and writes back through
  the select, which means product-info.js can keep swapping the whole <variant-selects>
  block — the replacement simply reconnects and rebuilds.
*/
(function () {
  if (customElements.get('pi-variant-dropdown')) return;
class PiVariantDropdown extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.trigger = this.querySelector('[data-pi-variant-trigger]');
    this.valueEl = this.querySelector('[data-pi-variant-value]');

    if (!this.select || !this.trigger || !this.valueEl) return;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onReposition = this.onReposition.bind(this);

    this.buildList();

    this.trigger.addEventListener('click', this.onTriggerClick.bind(this));
    this.trigger.addEventListener('keydown', this.onTriggerKeydown.bind(this));
    this.list.addEventListener('click', this.onListClick.bind(this));
    this.list.addEventListener('keydown', this.onListKeydown.bind(this));
    // Pointer over a value is a good signal it is about to be clicked, so product-info can have the
    // section on its way before then. Pointerover, not pointerenter, so it delegates from the list.
    this.list.addEventListener('pointerover', this.onListPointerOver.bind(this));

    // With the trigger live the select is decorative — keep it out of the tab order
    this.select.setAttribute('tabindex', '-1');
    this.select.setAttribute('aria-hidden', 'true');
    this.classList.add('pi-variants__select-wrap--ready');
  }

  disconnectedCallback() {
    this.close();
  }

  get options() {
    return Array.from(this.select.options);
  }

  /*
    Values with no variant for what is selected in the other options are left out of the list: with
    11 flavours across 2 sizes, the flavours only bottled in the other size are noise the shopper
    cannot act on. Sold out values stay, since those exist and can be notified about.

    They are skipped here rather than in the Liquid so the native select keeps every value —
    pi-variant-guard needs to be able to move this select onto any value when the option above it
    changes, and it cannot select an option that was never rendered.

    A currently selected value is always listed, even with no variant: it is what the trigger shows,
    and the list has to be able to mark it.
  */
  isRenderable(option) {
    if (option.selected) return true;
    if (this.closest('variant-selects')?.dataset.hideUnavailable !== 'true') return true;

    return option.dataset.valueState !== 'unavailable';
  }

  buildList() {
    if (this.list) this.list.remove();

    this.list = document.createElement('ul');
    this.list.className = 'pi-variants__list';
    this.list.setAttribute('role', 'listbox');
    this.list.setAttribute('tabindex', '-1');
    this.list.hidden = true;

    if (this.select.id) this.list.id = `${this.select.id}-listbox`;

       const entries = this.options.map((option, index) => ({ option, index }));

    if (this.dataset.sortByPrice === 'true') {
      const stateRank = (option) => {
        if (option.dataset.valueState === 'in_stock') return 0;
        if (option.dataset.valueState === 'sold_out') return 1;
        return 2;
      };

      const priceOf = (option) => {
        const value = option.dataset.sortPrice;
        const price = value == null || value.trim() === '' ? NaN : Number(value);
        return Number.isFinite(price) && price >= 0 ? price : Infinity;
      };

      entries.sort((a, b) => {
        const stateDifference = stateRank(a.option) - stateRank(b.option);
        if (stateDifference) return stateDifference;

        const firstPrice = priceOf(a.option);
        const secondPrice = priceOf(b.option);
        if (firstPrice < secondPrice) return -1;
        if (firstPrice > secondPrice) return 1;

        return a.index - b.index;
      });
    }

    entries.forEach(({ option, index }) => {
      if (!this.isRenderable(option)) return;

      const item = document.createElement('li');

      item.className = 'pi-variants__option';
      item.setAttribute('role', 'option');
      // The option's own index, not the item's: the two no longer line up once values are skipped,
      // and everything that selects or focuses reads it back through optionIndexOf/itemPositionOf.
      item.setAttribute('data-index', index);
      if (this.list.id) item.id = `${this.list.id}-option-${index}`;
      item.setAttribute('aria-selected', option.selected ? 'true' : 'false');

      /*
        With data-show-price set (bundle slots — see pi-bundle-picker.liquid) the
        row is split in two so the price can sit against the right edge: title
        span, price span. The price text is formatted in Liquid and carried on
        the option as data-price-label, so no money formatting is needed here.
        Without the flag the row stays a plain text node exactly as before.
      */
      const priceLabel = this.dataset.showPrice === 'true' ? option.dataset.priceLabel : '';

      if (priceLabel) {
        const label = document.createElement('span');

        label.className = 'pi-variants__option-label';
        label.textContent = option.textContent.trim();

        const price = document.createElement('span');

        price.className = 'pi-variants__option-price';
        price.textContent = priceLabel;

        item.append(label, price);
      } else {
        item.textContent = option.textContent.trim();
      }

      if (option.dataset.available === 'false') item.classList.add('pi-variants__option--sold-out');

      this.list.appendChild(item);
    });

    this.appendChild(this.list);
    this.trigger.setAttribute('aria-controls', this.list.id || '');
    this.syncValue();
  }

  optionIndexOf(position) {
    const item = this.items[position];

    return item ? parseInt(item.dataset.index, 10) : -1;
  }

  itemPositionOf(optionIndex) {
    return this.items.findIndex((item) => parseInt(item.dataset.index, 10) === optionIndex);
  }

           syncValue() {
    const selected = this.select.selectedOptions[0];

    if (selected) this.valueEl.textContent = selected.textContent.trim();
  }

  get items() {
    return Array.from(this.list.querySelectorAll('.pi-variants__option'));
  }

  get isOpen() {
    return !this.list.hidden;
  }

  open(focusIndex) {
    if (this.isOpen) return;

    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.position();

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
    window.addEventListener('resize', this.onReposition);
    window.addEventListener('scroll', this.onReposition, true);

    // focusItem works in list positions; the selected index is an option index, so it is mapped.
    const position = typeof focusIndex === 'number' ? focusIndex : this.itemPositionOf(this.select.selectedIndex);

    this.focusItem(position > -1 ? position : 0);
  }

  close(focusTrigger) {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    window.removeEventListener('resize', this.onReposition);
    window.removeEventListener('scroll', this.onReposition, true);

    if (!this.list || this.list.hidden) return;

    this.list.hidden = true;
    this.list.classList.remove('pi-variants__list--above');
    this.list.style.maxHeight = '';
    this.trigger.setAttribute('aria-expanded', 'false');

    if (focusTrigger) this.trigger.focus();
  }

  /*
    The panel is absolutely positioned, so what cuts it off is whichever overflow-clipping
    ancestor is tightest — the viewport on the product page, but the scroll box of the
    quick-add modal when the dropdown is rendered inside it. Measuring the viewport alone
    reports plenty of room below there, so the panel never flips and the modal clips it.
  */
  get clipBounds() {
    let top = 0;
    let bottom = window.innerHeight;

    // Intersect the viewport with every clipping ancestor rather than stopping at the
    // first one: an ancestor can clip while still being taller than the window (a section
    // with overflow: hidden, say), and there the viewport is still the real limit.
    for (let el = this.parentElement; el && el !== document.body; el = el.parentElement) {
      if (!/(auto|scroll|hidden|clip)/.test(getComputedStyle(el).overflowY)) continue;

      const box = el.getBoundingClientRect();

      top = Math.max(top, box.top);
      bottom = Math.min(bottom, box.bottom);
    }

    return { top, bottom };
  }

  // Flip the panel above the trigger when there's no room below it, and cap its height
  // when neither side can show it in full so it scrolls internally instead of clipping.
  position() {
    const GAP = 6;
    const EDGE = 10;

    this.list.style.maxHeight = '';

    const triggerBox = this.trigger.getBoundingClientRect();
    const bounds = this.clipBounds;
    const listHeight = this.list.offsetHeight;
    const spaceBelow = bounds.bottom - triggerBox.bottom - GAP - EDGE;
    const spaceAbove = triggerBox.top - bounds.top - GAP - EDGE;
    const above = spaceBelow < listHeight && spaceAbove > spaceBelow;

    this.list.classList.toggle('pi-variants__list--above', above);

    const available = above ? spaceAbove : spaceBelow;

    if (available < listHeight) this.list.style.maxHeight = `${Math.max(available, 120)}px`;
  }

  onReposition() {
    if (this.isOpen) this.position();
  }

  focusItem(index) {
    const items = this.items;
    const item = items[Math.max(0, Math.min(index, items.length - 1))];

    if (!item) return;

    this.activeIndex = items.indexOf(item);
    items.forEach((el) => el.classList.toggle('is-focused', el === item));
    if (item.id) this.list.setAttribute('aria-activedescendant', item.id);
    item.scrollIntoView({ block: 'nearest' });
    this.list.focus({ preventScroll: true });
  }

  // Takes an option index, not a list position — the caller reads it off the item's data-index.
  selectIndex(index) {
    const option = this.options[index];

    if (!option) return;

    this.items.forEach((item) =>
      item.setAttribute('aria-selected', parseInt(item.dataset.index, 10) === index ? 'true' : 'false')
    );

    if (this.select.selectedIndex !== index) {
      this.select.selectedIndex = index;
      // VariantSelects moves the `selected` attribute and republishes from here
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.syncValue();
    this.close(true);
  }

  onTriggerClick(event) {
    event.preventDefault();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  onTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.open();
    }
  }

  onListClick(event) {
    const item = event.target.closest('.pi-variants__option');

    if (!item) return;

    this.selectIndex(parseInt(item.dataset.index, 10));
  }

  onListPointerOver(event) {
    const item = event.target.closest('.pi-variants__option');

    if (!item) return;

    const option = this.options[parseInt(item.dataset.index, 10)];

    if (!option || option.selected) return;

    this.dispatchEvent(
      new CustomEvent('pi-variant:prefetch', {
        bubbles: true,
        detail: { select: this.select, option },
      })
    );
  }

  onListKeydown(event) {
    const lastIndex = this.items.length - 1;
    const active = typeof this.activeIndex === 'number' ? this.activeIndex : 0;
    let target = null;

    if (event.key === 'ArrowDown') target = Math.min(active + 1, lastIndex);
    if (event.key === 'ArrowUp') target = Math.max(active - 1, 0);
    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = lastIndex;

    if (target !== null) {
      event.preventDefault();
      this.focusItem(target);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectIndex(this.optionIndexOf(active));
      return;
    }

    if (event.key === 'Tab') this.close();
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
    }
  }
}

// if (!customElements.get('pi-variant-dropdown')) {
//   customElements.define('pi-variant-dropdown', PiVariantDropdown);
// }
 customElements.define('pi-variant-dropdown', PiVariantDropdown);
})();